<template>
  <div class="retention-defaults">
    <retention-tabs />

    <div class="page-header">
      <div>
        <h2>Default Retention Messages</h2>
        <p class="hint">
          Apple shows a default whenever the realtime call fails or isn't configured.
          Switch between By Product (per-product locale slots) and By Locale (apply one message to many products).
        </p>
      </div>
      <span v-if="!canConfigureRetentionDefaultMessages" class="permission-note">
        No permission to configure defaults
      </span>
    </div>

    <div class="view-toggle">
      <button
        class="toggle-btn"
        :class="{ active: activeView === 'product' }"
        @click="setView('product')"
      >
        <i class="fas fa-box"></i>
        By Product
      </button>
      <button
        class="toggle-btn"
        :class="{ active: activeView === 'locale' }"
        @click="setView('locale')"
      >
        <i class="fas fa-globe"></i>
        By Locale
      </button>
    </div>

    <defaults-by-product v-if="activeView === 'product'" />
    <defaults-by-locale v-else />
  </div>
</template>

<script>
import RetentionTabs from './Tabs.vue';
import DefaultsByProduct from './DefaultsByProduct.vue';
import DefaultsByLocale from './DefaultsByLocale.vue';
import { permissionsMixin } from '../../mixins/permissions.mixin';

export default {
  mixins: [permissionsMixin],
  components: { RetentionTabs, DefaultsByProduct, DefaultsByLocale },
  data() {
    return {
      activeView: 'product'
    };
  },
  watch: {
    '$route.query.view': {
      immediate: false,
      handler(v) {
        this.activeView = v === 'locale' ? 'locale' : 'product';
      }
    }
  },
  async mounted() {
    if (this.$route.query.view === 'locale') {
      this.activeView = 'locale';
    }
    await Promise.all([
      this.$store.dispatch('retention/fetchDefaults'),
      this.$store.dispatch('retention/fetchProducts'),
      this.$store.dispatch('retention/fetchMessages')
    ]);
  },
  methods: {
    setView(v) {
      if (this.activeView === v) return;
      this.activeView = v;
      const nextQuery = { ...this.$route.query };
      if (v === 'product') {
        delete nextQuery.view;
        delete nextQuery.locale;
      } else {
        nextQuery.view = 'locale';
      }
      this.$router.replace({ query: nextQuery }).catch(() => {});
    }
  }
};
</script>

<style scoped>
.retention-defaults {
  flex: 1;
  min-width: 0;
  padding: 0.5rem 0.25rem;
  color: rgba(255, 255, 255, 0.88);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.85rem;
}

.page-header h2 {
  margin: 0 0 0.3rem;
  font-size: 1.5rem;
  color: #fff;
  font-weight: 600;
}

.hint {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.85rem;
  margin: 0;
  max-width: 80ch;
  line-height: 1.4;
}

.permission-note {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
}

.view-toggle {
  display: inline-flex;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 3px;
  margin-bottom: 0.85rem;
  gap: 2px;
}

.toggle-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.85rem;
  font-weight: 500;
  padding: 0.45rem 0.95rem;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.toggle-btn i {
  font-size: 0.78rem;
  opacity: 0.9;
}

.toggle-btn:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.06);
}

.toggle-btn.active {
  background: #03B3DA;
  color: #fff;
}

.toggle-btn.active:hover {
  background: #04a3c5;
}
</style>
